<template>
    <div class="h-full w-full flex flex-col">
        <!-- Header with Entity Filter -->
        <div class="mb-6">
            <div class="flex flex-col sm:flex-row justify-between items-start sm:items-center gap-4">
                <h2 class="text-2xl font-bold text-gray-800">Activities Calendar 2025-2026</h2>
                <div class="flex items-center gap-4">
                    <div class="flex items-center gap-2">
                        <label class="text-sm font-medium text-gray-700">Entity:</label>
                        <select v-model="selectedEntity"
                            class="border border-gray-300 rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-primary focus:border-transparent">
                            <option value="">All</option>
                            <option value="ADPOLY">ADPOLY</option>
                            <option value="FCHS">FCHS</option>
                            <option value="ATHS">ATS</option>
                        </select>
                    </div>
                    <div class="flex items-center gap-2">
                        <label class="text-sm font-medium text-gray-700">View:</label>
                        <div class="flex border border-gray-300 rounded-lg overflow-hidden">
                            <button @click="viewMode = 'list'"
                                :class="viewMode === 'list' ? 'bg-primary text-white' : 'bg-white text-gray-700 hover:bg-gray-50'"
                                class="px-4 py-2 text-sm font-medium transition-colors duration-200">
                                <font-awesome-icon icon="fa-solid fa-list" class="mr-2"></font-awesome-icon>
                                List
                            </button>
                            <button @click="viewMode = 'calendar'"
                                :class="viewMode === 'calendar' ? 'bg-primary text-white' : 'bg-white text-gray-700 hover:bg-gray-50'"
                                class="px-4 py-2 text-sm font-medium transition-colors duration-200">
                                <font-awesome-icon icon="fa-solid fa-calendar-days" class="mr-2"></font-awesome-icon>
                                Calendar
                            </button>
                        </div>
                    </div>
                </div>
            </div>
        </div>

        <!-- Content Area -->
        <transition name="fade" mode="out-in">
            <div v-if="viewMode === 'list'" key="list" class="flex-1">
                <!-- Show content if there are filtered activities -->
                <div v-if="hasFilteredActivities" class="grid grid-cols-1 lg:grid-cols-2 gap-6">
                    <!-- Activities grouped by entity -->
                    <div v-for="entity in getFilteredEntities" :key="entity"
                        class="bg-white rounded-xl shadow-lg overflow-hidden">
                        <div class="bg-1 text-white p-4">
                            <div class="flex items-center justify-between">
                                <div>
                                    <h3 class="text-xl font-bold">{{ getEntityName(entity) }}</h3>
                                    <p class="text-blue-100 text-sm">{{ getFilteredActivitiesByEntity(entity).length }}
                                        Activities</p>
                                </div>
                                <div class="flex items-center">
                                    <div class="w-3 h-3 rounded-full bg-white mr-2"></div>
                                    <span class="text-xs font-medium text-blue-100 uppercase tracking-wide">{{ entity
                                    }}</span>
                                </div>
                            </div>
                        </div>
                        <div class="p-6">
                            <div class="space-y-3">
                                <activities-calendar-card v-for="activity in getFilteredActivitiesByEntity(entity)"
                                    :key="activity.id" :activity="activity"></activities-calendar-card>
                            </div>
                        </div>
                    </div>
                </div>

                <!-- No Data Message -->
                <div v-else class="flex flex-col items-center justify-center py-16 px-4">
                    <div class="text-center max-w-md">
                        <!-- Icon -->
                        <div class="mx-auto flex items-center justify-center h-20 w-20 rounded-full bg-gray-100 mb-6">
                            <font-awesome-icon icon="fa-solid fa-calendar-xmark"
                                class="h-10 w-10 text-gray-400"></font-awesome-icon>
                        </div>

                        <!-- Title -->
                        <h3 class="text-xl font-semibold text-gray-900 mb-2">
                            No Activities Found
                        </h3>

                        <!-- Description -->
                        <p class="text-gray-600 mb-6">
                            <span v-if="selectedEntity && selectedEntity !== ''">
                                No activities are available for <strong>{{ getEntityName(selectedEntity) }}</strong> at
                                this time.
                            </span>
                            <span v-else>
                                No activities are available for the selected filters.
                            </span>
                        </p>

                        <!-- Action Buttons -->
                        <div class="flex flex-col sm:flex-row gap-3 justify-center">
                            <button @click="selectedEntity = ''"
                                class="inline-flex items-center px-4 py-2 border border-transparent text-sm font-medium rounded-md text-white bg-primary hover:bg-primary-dark focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-primary transition-colors duration-200">
                                <font-awesome-icon icon="fa-solid fa-eye" class="mr-2"></font-awesome-icon>
                                View All Entities
                            </button>
                        </div>

                        <!-- Additional Info -->
                        <div class="mt-6 p-4 bg-blue-50 rounded-lg">
                            <p class="text-sm text-blue-700">
                                <font-awesome-icon icon="fa-solid fa-info-circle" class="mr-2"></font-awesome-icon>
                                Activities data will be updated as it becomes available from each institution.
                            </p>
                        </div>
                    </div>
                </div>
            </div>

            <div v-else key="calendar" class="flex-1">
                <div v-if="hasFilteredActivities" class="bg-white rounded-xl shadow-lg overflow-hidden">
                    <div class="p-6">
                        <FullCalendar ref="fullCalendar" class="demo-app-calendar" :options="calendarOptions">
                            <template v-slot:eventContent="arg">
                                <div class="truncate text-xs font-medium">
                                    {{ arg.event.title }}
                                </div>
                            </template>
                        </FullCalendar>
                    </div>
                </div>

                <!-- No Data Message for Calendar View -->
                <div v-else class="flex flex-col items-center justify-center py-16 px-4">
                    <div class="text-center max-w-md">
                        <!-- Icon -->
                        <div class="mx-auto flex items-center justify-center h-20 w-20 rounded-full bg-gray-100 mb-6">
                            <font-awesome-icon icon="fa-solid fa-calendar-xmark"
                                class="h-10 w-10 text-gray-400"></font-awesome-icon>
                        </div>

                        <!-- Title -->
                        <h3 class="text-xl font-semibold text-gray-900 mb-2">
                            No Calendar Activities
                        </h3>

                        <!-- Description -->
                        <p class="text-gray-600 mb-6">
                            <span v-if="selectedEntity && selectedEntity !== ''">
                                No activities are available for <strong>{{ getEntityName(selectedEntity) }}</strong> at
                                this time.
                            </span>
                            <span v-else>
                                No activities are available for the selected filters.
                            </span>
                        </p>

                        <!-- Action Buttons -->
                        <div class="flex flex-col sm:flex-row gap-3 justify-center">
                            <button @click="selectedEntity = ''"
                                class="inline-flex items-center px-4 py-2 border border-transparent text-sm font-medium rounded-md text-white bg-primary hover:bg-primary-dark focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-primary transition-colors duration-200">
                                <font-awesome-icon icon="fa-solid fa-eye" class="mr-2"></font-awesome-icon>
                                View All Entities
                            </button>
                        </div>
                    </div>
                </div>
            </div>
        </transition>
    </div>
</template>

<script>
import FullCalendar from '@fullcalendar/vue3'
import dayGridPlugin from '@fullcalendar/daygrid'
import timeGridPlugin from '@fullcalendar/timegrid'
import interactionPlugin from '@fullcalendar/interaction'
import ActivitiesCalendarCard from '../../components/events/ActivitiesCalendarCard.vue'

export default {
    name: "activities-calendar",
    components: {
        FullCalendar,
        ActivitiesCalendarCard,
    },
    data() {
        return {
            selectedEntity: '',
            viewMode: 'list',
            calendarOptions: {
                plugins: [
                    dayGridPlugin,
                    timeGridPlugin,
                    interactionPlugin
                ],
                headerToolbar: {
                    left: 'prev,next today',
                    center: 'title',
                    right: 'dayGridMonth,timeGridWeek,timeGridDay'
                },
                initialView: 'dayGridMonth',
                editable: false,
                selectable: true,
                selectMirror: true,
                dayMaxEvents: true,
                weekends: true,
                events: [],
                eventDisplay: 'block',
                eventColor: '#394867',
                eventTextColor: '#ffffff',
                height: 'auto',
            },
            // Static Activities Data from Screenshots
            activities: [
                // ADPOLY Activities (from screenshot) - Sorted by startDate
                {
                    id: 1,
                    title: 'Student Orientation',
                    description: 'Orientation session for new students',
                    startDate: null,
                    endDate: null,
                    entity: 'ADPOLY',
                    department: null,
                    eventType: 'ADPOLY General Events',
                    isGeneralActivity: true,
                    color: '#38a169',
                    location: 'MBZ Auditorium',
                    startTime: null,
                    endTime: null,
                    targetAudience: null
                },
                {
                    id: 2,
                    title: 'Partnership Orientation (AD campus)',
                    description: 'Partnership orientation session for Abu Dhabi campus',
                    startDate: '2025-09-02',
                    endDate: '2025-09-02',
                    entity: 'ADPOLY',
                    department: null,
                    eventType: 'ADPOLY General Events',
                    isGeneralActivity: true,
                    color: '#38a169',
                    location: 'Online',
                    startTime: '12:00:00',
                    endTime: '13:00:00',
                    targetAudience: null
                },
                {
                    id: 3,
                    title: 'Partnership Orientation (AA campus)',
                    description: 'Partnership orientation session for Al Ain campus',
                    startDate: '2025-09-04',
                    endDate: '2025-09-04',
                    entity: 'ADPOLY',
                    department: null,
                    eventType: 'ADPOLY General Events',
                    isGeneralActivity: true,
                    color: '#38a169',
                    location: 'Online',
                    startTime: '12:00:00',
                    endTime: '13:00:00',
                    targetAudience: null
                },
                {
                    id: 4,
                    title: 'Student Welcome',
                    description: 'Welcome event for students',
                    startDate: '2025-09-11',
                    endDate: '2025-09-11',
                    entity: 'ADPOLY',
                    department: null,
                    eventType: 'ADPOLY General Events',
                    isGeneralActivity: true,
                    color: '#38a169',
                    location: '2nd Floor',
                    startTime: '11:00:00',
                    endTime: '13:00:00',
                    targetAudience: null
                },
                {
                    id: 5,
                    title: 'Student Council Election',
                    description: 'Student council election process',
                    startDate: '2025-09-15',
                    endDate: '2025-09-30',
                    entity: 'ADPOLY',
                    department: null,
                    eventType: 'ADPOLY General Events',
                    isGeneralActivity: true,
                    color: '#38a169',
                    location: null,
                    time: null,
                    targetAudience: null
                },
                {
                    id: 6,
                    title: 'How to write a literature review',
                    description: 'Academic session on writing literature reviews',
                    startDate: '2025-09-23',
                    endDate: '2025-09-23',
                    entity: 'ADPOLY',
                    department: null,
                    eventType: 'Academic Sessions',
                    isGeneralActivity: false,
                    color: '#38a169',
                    location: 'Online (Teams)',
                    startTime: '12:00:00',
                    endTime: '13:00:00',
                    targetAudience: null
                },
                {
                    id: 7,
                    title: 'Cybersecurity in Focus: Growth, Opportunities, and the Future Landscape',
                    description: 'Specialized departmental training on cybersecurity trends and opportunities',
                    startDate: '2025-09-24',
                    endDate: '2025-09-24',
                    entity: 'ADPOLY',
                    department: null,
                    eventType: 'Specialized Departmental Training and Events',
                    isGeneralActivity: false,
                    color: '#38a169',
                    location: 'ADPOLY Auditorium-Abu Dhabi',
                    startTime: '09:00:00',
                    endTime: '10:00:00',
                    targetAudience: null
                },
                {
                    id: 8,
                    title: 'OJT awareness Session',
                    description: 'On-the-job training awareness session',
                    startDate: '2025-09-25',
                    endDate: '2025-09-25',
                    entity: 'ADPOLY',
                    department: null,
                    eventType: 'ADPOLY General Events',
                    isGeneralActivity: true,
                    color: '#38a169',
                    location: 'ADPOLY Auditorium-Abu Dhabi',
                    startTime: '12:00:00',
                    endTime: '13:00:00',
                    targetAudience: null
                },
                {
                    id: 9,
                    title: 'Expo-Science International 2025',
                    description: 'International science exhibition participation',
                    startDate: '2025-09-29',
                    endDate: '2025-10-01',
                    entity: 'ADPOLY',
                    department: null,
                    eventType: 'Field Trips/Participation',
                    isGeneralActivity: false,
                    color: '#38a169',
                    location: 'ADNEC',
                    startTime: '00:00:00',
                    endTime: '23:59:59',
                    targetAudience: null
                },
                {
                    id: 10,
                    title: 'WETEX (Water, Energy, Technology, and Environment Exhibition)',
                    description: 'Water, Energy, Technology, and Environment Exhibition',
                    startDate: '2025-09-30',
                    endDate: '2025-10-02',
                    entity: 'ADPOLY',
                    department: null,
                    eventType: 'Field Trips',
                    isGeneralActivity: false,
                    color: '#38a169',
                    location: 'Dubai World Trade Center',
                    startTime: '00:00:00',
                    endTime: '23:59:59',
                    targetAudience: null
                },
                {
                    id: 16,
                    title: 'Breast Cancer Awareness Day',
                    description: 'Breast cancer awareness and education event',
                    startDate: '2025-10-01',
                    endDate: '2025-10-31',
                    entity: 'ADPOLY',
                    department: null,
                    eventType: 'ADPOLY General Events',
                    isGeneralActivity: true,
                    color: '#38a169',
                    location: 'ADPOLY Building-Abu Dhabi',
                    time: null,
                    targetAudience: null
                },
                {
                    id: 11,
                    title: 'Teacher\'s Day Celebration',
                    description: 'Celebration of Teacher\'s Day',
                    startDate: '2025-10-06',
                    endDate: '2025-10-06',
                    entity: 'ADPOLY',
                    department: null,
                    eventType: 'ADPOLY General Events',
                    isGeneralActivity: true,
                    color: '#38a169',
                    location: 'ADPOLY Building-Abu Dhabi',
                    time: null,
                    targetAudience: null
                },
                {
                    id: 12,
                    title: 'ADPOLY Club Fair',
                    description: 'Club fair showcasing various student clubs and organizations',
                    startDate: '2025-10-08',
                    endDate: '2025-10-08',
                    entity: 'ADPOLY',
                    department: null,
                    eventType: 'ADPOLY General Events',
                    isGeneralActivity: true,
                    color: '#38a169',
                    location: 'AD & AA campuses',
                    startTime: '12:00:00',
                    endTime: '15:00:00',
                    targetAudience: null
                },
                {
                    id: 13,
                    title: 'GITEX GLOBAL 2025',
                    description: 'Global Information Technology Exhibition',
                    startDate: '2025-10-13',
                    endDate: '2025-10-17',
                    entity: 'ADPOLY',
                    department: null,
                    eventType: 'Field Trip',
                    isGeneralActivity: false,
                    color: '#38a169',
                    location: 'Dubai World Trade Center',
                    time: null,
                    targetAudience: null
                },
                {
                    id: 14,
                    title: 'Najah Exhibition',
                    description: 'Najah education and career exhibition',
                    startDate: '2025-10-19',
                    endDate: '2025-10-21',
                    entity: 'ADPOLY',
                    department: null,
                    eventType: 'ADPOLY General Events',
                    isGeneralActivity: true,
                    color: '#38a169',
                    location: 'ADNEC',
                    time: null,
                    targetAudience: null
                },
                {
                    id: 15,
                    title: 'How to write a report for graduation project',
                    description: 'Academic session on writing graduation project reports',
                    startDate: '2025-10-28',
                    endDate: '2025-10-28',
                    entity: 'ADPOLY',
                    department: null,
                    eventType: 'Academic Sessions',
                    isGeneralActivity: false,
                    color: '#38a169',
                    location: 'Online',
                    startTime: '12:00:00',
                    endTime: '13:00:00',
                    targetAudience: null
                },
                {
                    id: 17,
                    title: 'How to Build your LinkedIn Profile',
                    description: 'Academic session on building professional LinkedIn profiles',
                    startDate: '2025-10-29',
                    endDate: '2025-10-29',
                    entity: 'ADPOLY',
                    department: null,
                    eventType: 'Academic Sessions',
                    isGeneralActivity: false,
                    color: '#38a169',
                    location: 'Online',
                    startTime: '12:00:00',
                    endTime: '13:00:00',
                    targetAudience: null
                },
                {
                    id: 25,
                    title: 'Alumni Day',
                    description: 'Alumni Day celebration',
                    startDate: '2025-11-01',
                    endDate: '2025-11-30',
                    entity: 'ADPOLY',
                    department: null,
                    eventType: 'ADPOLY General Events',
                    isGeneralActivity: true,
                    color: '#38a169',
                    location: 'ADPOLY Auditorium-Abu Dhabi',
                    time: null,
                    targetAudience: null
                },
                {
                    id: 28,
                    title: 'National Day Celebration',
                    description: 'UAE National Day celebration',
                    startDate: '2025-11-01',
                    endDate: '2025-11-30',
                    entity: 'ADPOLY',
                    department: null,
                    eventType: 'ADPOLY General Events',
                    isGeneralActivity: true,
                    color: '#38a169',
                    location: 'ADPOLY MBZ & AA',
                    startTime: '00:00:00',
                    endTime: '23:59:59',
                    targetAudience: null
                },
                {
                    id: 18,
                    title: 'ADIPEC 2024',
                    description: 'Abu Dhabi International Petroleum Exhibition and Conference',
                    startDate: '2025-11-03',
                    endDate: '2025-11-06',
                    entity: 'ADPOLY',
                    department: null,
                    eventType: 'Field Trip/Participation/PET',
                    isGeneralActivity: false,
                    color: '#38a169',
                    location: 'ADNEC',
                    time: null,
                    targetAudience: null
                },
                {
                    id: 19,
                    title: 'Flag Day',
                    description: 'UAE Flag Day celebration',
                    startDate: '2025-11-03',
                    endDate: '2025-11-03',
                    entity: 'ADPOLY',
                    department: null,
                    eventType: 'ADPOLY General Events',
                    isGeneralActivity: true,
                    color: '#38a169',
                    location: 'ADPOLY MBZ & AA',
                    time: null,
                    targetAudience: null
                },
                {
                    id: 20,
                    title: 'Programming in the digital world',
                    description: 'Open mic discussion on programming in the digital world',
                    startDate: '2025-11-05',
                    endDate: '2025-11-05',
                    entity: 'ADPOLY',
                    department: null,
                    eventType: 'Open Mic Discussion',
                    isGeneralActivity: false,
                    color: '#38a169',
                    location: 'Canteen Area 1st Floor',
                    startTime: '11:00:00',
                    endTime: '13:00:00',
                    targetAudience: null
                },
                {
                    id: 21,
                    title: 'Presentation and Interview Skills for GP',
                    description: 'Academic session on presentation and interview skills for graduation projects',
                    startDate: '2025-11-10',
                    endDate: '2025-11-10',
                    entity: 'ADPOLY',
                    department: null,
                    eventType: 'Academic Sessions',
                    isGeneralActivity: false,
                    color: '#38a169',
                    location: 'Online',
                    startTime: '12:00:00',
                    endTime: '13:00:00',
                    targetAudience: null
                },
                {
                    id: 22,
                    title: 'How to prepare a professional CV',
                    description: 'Academic session on preparing professional CVs',
                    startDate: '2025-11-12',
                    endDate: '2025-11-12',
                    entity: 'ADPOLY',
                    department: null,
                    eventType: 'Academic Sessions',
                    isGeneralActivity: false,
                    color: '#38a169',
                    location: 'Online',
                    startTime: '12:00:00',
                    endTime: '13:01:00',
                    targetAudience: null
                },
                {
                    id: 24,
                    title: 'Dubai Airshow 2025',
                    description: 'Dubai Airshow exhibition',
                    startDate: '2025-11-17',
                    endDate: '2025-11-21',
                    entity: 'ADPOLY',
                    department: null,
                    eventType: 'Field Trips',
                    isGeneralActivity: false,
                    color: '#38a169',
                    location: 'DWC, Dubai Airshow Site',
                    startTime: '00:00:00',
                    endTime: '23:59:59',
                    targetAudience: null
                },
                {
                    id: 23,
                    title: 'Tawdheef',
                    description: 'Tawdheef career exhibition',
                    startDate: '2025-11-18',
                    endDate: '2025-11-20',
                    entity: 'ADPOLY',
                    department: null,
                    eventType: 'Field Trips',
                    isGeneralActivity: false,
                    color: '#38a169',
                    location: 'ADNEC',
                    startTime: '00:00:00',
                    endTime: '23:59:59',
                    targetAudience: null
                },
                {
                    id: 26,
                    title: 'GP Exhibition Fall-2024 & Mock Interviews',
                    description: 'Graduation Project Exhibition Fall 2024 and Mock Interviews',
                    startDate: '2025-11-20',
                    endDate: '2025-11-20',
                    entity: 'ADPOLY',
                    department: null,
                    eventType: 'ADPOLY General Events',
                    isGeneralActivity: true,
                    color: '#38a169',
                    location: 'ADPOLY Building-Abu Dhabi',
                    time: null,
                    targetAudience: null
                },
                {
                    id: 27,
                    title: 'Martyr\'s Day',
                    description: 'Martyr\'s Day commemoration',
                    startDate: '2025-11-30',
                    endDate: '2025-11-30',
                    entity: 'ADPOLY',
                    department: null,
                    eventType: 'ADPOLY General Events',
                    isGeneralActivity: true,
                    color: '#38a169',
                    location: 'ADPOLY MBZ & AA',
                    time: null,
                    targetAudience: null
                },
                {
                    id: 29,
                    title: 'Orientation Day',
                    description: 'Orientation day for new students',
                    startDate: '2025-12-29',
                    endDate: '2026-01-02',
                    entity: 'ADPOLY',
                    department: null,
                    eventType: 'ADPOLY General Events/New Ss',
                    isGeneralActivity: true,
                    color: '#38a169',
                    location: 'ADPOLY Auditorium-Abu Dhabi, Al Ain Campus-Library',
                    time: null,
                    targetAudience: null
                },
                {
                    id: 31,
                    title: 'Volunteers Awareness',
                    description: 'Volunteers awareness event',
                    startDate: '2026-01-01',
                    endDate: '2026-01-31',
                    entity: 'ADPOLY',
                    department: null,
                    eventType: 'ADPOLY General Events',
                    isGeneralActivity: true,
                    color: '#38a169',
                    location: 'ADPOLY Auditorium-Abu Dhabi',
                    time: null,
                    targetAudience: null
                },
                {
                    id: 30,
                    title: 'WFES 2026 - World Future Energy Summit',
                    description: 'World Future Energy Summit 2026',
                    startDate: '2026-01-13',
                    endDate: '2026-01-15',
                    entity: 'ADPOLY',
                    department: null,
                    eventType: 'Field Trips',
                    isGeneralActivity: false,
                    color: '#38a169',
                    location: 'ADNEC',
                    startTime: '00:00:00',
                    endTime: '23:59:59',
                    targetAudience: null
                },
                {
                    id: 32,
                    title: 'How to write a literature review for GP',
                    description: 'Academic session on writing literature reviews for graduation projects',
                    startDate: '2026-01-21',
                    endDate: '2026-01-21',
                    entity: 'ADPOLY',
                    department: null,
                    eventType: 'Academic Sessions',
                    isGeneralActivity: false,
                    color: '#38a169',
                    location: 'Online',
                    startTime: '12:00:00',
                    endTime: '13:00:00',
                    targetAudience: null
                },
                {
                    id: 33,
                    title: 'Club Fair',
                    description: 'Club fair showcasing various student clubs and organizations',
                    startDate: '2026-01-22',
                    endDate: '2026-01-22',
                    entity: 'ADPOLY',
                    department: null,
                    eventType: 'ADPOLY General Events',
                    isGeneralActivity: true,
                    color: '#38a169',
                    location: 'Canteen Area 1st Floor',
                    startTime: '12:00:00',
                    endTime: '15:00:00',
                    targetAudience: null
                },
                {
                    id: 34,
                    title: 'How to prepare a report for GP',
                    description: 'Academic session on preparing reports for graduation projects',
                    startDate: '2026-01-28',
                    endDate: '2026-01-28',
                    entity: 'ADPOLY',
                    department: null,
                    eventType: 'Academic Sessions',
                    isGeneralActivity: false,
                    color: '#38a169',
                    location: 'Online',
                    startTime: '12:00:00',
                    endTime: '13:00:00',
                    targetAudience: null
                },
                {
                    id: 35,
                    title: 'Innovation Day',
                    description: 'Innovation Day celebration',
                    startDate: '2026-02-04',
                    endDate: '2026-02-04',
                    entity: 'ADPOLY',
                    department: null,
                    eventType: 'ADPOLY General Events',
                    isGeneralActivity: true,
                    color: '#38a169',
                    location: 'ADPOLY Building-Both Campuses',
                    time: null,
                    targetAudience: null
                },
                {
                    id: 36,
                    title: 'How to prepare a professional CV',
                    description: 'Academic session on preparing professional CVs',
                    startDate: '2026-02-11',
                    endDate: '2026-02-11',
                    entity: 'ADPOLY',
                    department: null,
                    eventType: 'Academic Sessions',
                    isGeneralActivity: false,
                    color: '#38a169',
                    location: 'Online',
                    startTime: '12:00:00',
                    endTime: '13:00:00',
                    targetAudience: null
                },
                {
                    id: 37,
                    title: 'Reading Month Activities',
                    description: 'Reading month activities throughout March',
                    startDate: '2026-03-01',
                    endDate: '2026-03-31',
                    entity: 'ADPOLY',
                    department: null,
                    eventType: 'ADPOLY General Events',
                    isGeneralActivity: true,
                    color: '#38a169',
                    location: 'ADPOLY Auditorium-Abu Dhabi, Al Ain Campus-Library',
                    time: null,
                    targetAudience: null
                },
                {
                    id: 38,
                    title: 'ADPOLY PD Week',
                    description: 'ADPOLY Professional Development Week',
                    startDate: '2026-03-23',
                    endDate: '2026-03-27',
                    entity: 'ADPOLY',
                    department: null,
                    eventType: 'Academic Sessions/Staff',
                    isGeneralActivity: false,
                    color: '#38a169',
                    location: 'ADPOLY Auditorium-Abu Dhabi',
                    time: null,
                },
                {
                    id: 39,
                    title: 'Emirates Skills National Competition',
                    description: 'Emirates Skills National Competition',
                    startDate: '2026-03-30',
                    endDate: '2026-04-01',
                    entity: 'ADPOLY',
                    department: null,
                    eventType: 'Competition',
                    isGeneralActivity: false,
                    color: '#38a169',
                    location: 'ADNEC',
                    startTime: '00:00:00',
                    endTime: '23:59:59',
                    targetAudience: null
                },
                {
                    id: 40,
                    title: 'ADPOLY Spring \'26 Bootcamp',
                    description: 'ADPOLY Spring 2026 Bootcamp',
                    startDate: '2026-04-01',
                    endDate: '2026-04-30',
                    entity: 'ADPOLY',
                    department: null,
                    eventType: 'Competition/ISET',
                    isGeneralActivity: false,
                    color: '#38a169',
                    location: 'Cyber Valley',
                    time: null,
                    targetAudience: null
                },
                {
                    id: 41,
                    title: 'Obesity Awareness Day',
                    description: 'Obesity awareness day',
                    startDate: '2026-04-01',
                    endDate: '2026-04-30',
                    entity: 'ADPOLY',
                    department: null,
                    eventType: 'ADPOLY General Events',
                    isGeneralActivity: true,
                    color: '#38a169',
                    location: 'ADPOLY Building-Both Campuses',
                    time: null,
                    targetAudience: null
                },
                {
                    id: 42,
                    title: 'Film Festival',
                    description: 'Film festival event',
                    startDate: '2026-04-01',
                    endDate: '2026-04-30',
                    entity: 'ADPOLY',
                    department: null,
                    eventType: 'ADPOLY General Events',
                    isGeneralActivity: true,
                    color: '#38a169',
                    location: 'ADPOLY Auditorium',
                    startTime: '11:00:00',
                    endTime: '13:00:00',
                    targetAudience: null
                },
                {
                    id: 43,
                    title: 'Open Day',
                    description: 'Open day for visitors',
                    startDate: '2026-04-01',
                    endDate: '2026-04-30',
                    entity: 'ADPOLY',
                    department: null,
                    eventType: 'ADPOLY General Events',
                    isGeneralActivity: true,
                    color: '#38a169',
                    location: 'ADPOLY Building-Both Campuses',
                    startTime: '00:00:00',
                    endTime: '23:59:59',
                    targetAudience: null
                },
                {
                    id: 45,
                    title: 'GP Presentations and Mockup Interviews',
                    description: 'Graduation Project presentations and mockup interviews',
                    startDate: '2026-04-01',
                    endDate: '2026-04-30',
                    entity: 'ADPOLY',
                    department: null,
                    eventType: 'ADPOLY General Events',
                    isGeneralActivity: true,
                    color: '#38a169',
                    location: 'ADPOLY Building-Abu Dhabi',
                    startTime: '00:00:00',
                    endTime: '23:59:59',
                    targetAudience: null
                },
                {
                    id: 44,
                    title: 'Presentation and Interview Skills for GP',
                    description: 'Academic session on presentation and interview skills for graduation projects',
                    startDate: '2026-04-08',
                    endDate: '2026-04-08',
                    entity: 'ADPOLY',
                    department: null,
                    eventType: 'Academic Sessions',
                    isGeneralActivity: false,
                    color: '#38a169',
                    location: 'Online',
                    startTime: '12:00:00',
                    endTime: '13:00:00',
                    targetAudience: null
                },
                {
                    id: 46,
                    title: 'Labor Day',
                    description: 'Labor Day celebration',
                    startDate: '2026-05-01',
                    endDate: '2026-05-01',
                    entity: 'ADPOLY',
                    department: null,
                    eventType: 'ADPOLY General Events',
                    isGeneralActivity: true,
                    color: '#38a169',
                    location: 'ADPOLY Both Campuses',
                    time: null,
                    targetAudience: null
                },
                {
                    id: 47,
                    title: 'GP Exhibition Spring-2026/Career Day',
                    description: 'Graduation Project Exhibition Spring 2026 and Career Day',
                    startDate: '2026-05-01',
                    endDate: '2026-05-31',
                    entity: 'ADPOLY',
                    department: null,
                    eventType: 'ADPOLY General Events',
                    isGeneralActivity: true,
                    color: '#38a169',
                    location: 'ADPOLY Building-Abu Dhabi',
                    startTime: '00:00:00',
                    endTime: '23:59:59',
                    targetAudience: null
                },
                {
                    id: 48,
                    title: 'Sustainability Day Celebration',
                    description: 'Sustainability Day celebration',
                    startDate: '2026-05-01',
                    endDate: '2026-05-31',
                    entity: 'ADPOLY',
                    department: null,
                    eventType: 'ADPOLY General Events',
                    isGeneralActivity: true,
                    color: '#38a169',
                    location: 'ADPOLY Building-Both Campuses',
                    startTime: '12:00:00',
                    endTime: '15:00:00',
                    targetAudience: null
                },
                {
                    id: 49,
                    title: 'GISEC',
                    description: 'GISEC exhibition',
                    startDate: '2026-05-05',
                    endDate: '2026-05-05',
                    entity: 'ADPOLY',
                    department: null,
                    eventType: 'Field Trip/ISET',
                    isGeneralActivity: false,
                    color: '#38a169',
                    location: 'ADPOLY Auditorium-Abu Dhabi',
                    startTime: '00:00:00',
                    endTime: '23:59:59',
                    targetAudience: null
                },
                {
                    id: 50,
                    title: 'ADPOLY Cyber Security Hackathon June 2026',
                    description: 'ADPOLY Cyber Security Hackathon June 2026',
                    startDate: '2026-06-01',
                    endDate: '2026-06-30',
                    entity: 'ADPOLY',
                    department: null,
                    eventType: 'Competition/ISET',
                    isGeneralActivity: false,
                    color: '#38a169',
                    location: 'Cyber Valley',
                    time: null,
                    targetAudience: null
                },
                {
                    id: 51,
                    title: 'Open Day',
                    description: 'Open day for visitors',
                    startDate: '2026-06-01',
                    endDate: '2026-06-30',
                    entity: 'ADPOLY',
                    department: null,
                    eventType: 'ADPOLY General Events',
                    isGeneralActivity: true,
                    color: '#38a169',
                    location: 'ADPOLY Building-Both Campuses',
                    startTime: '00:00:00',
                    endTime: '23:59:59',
                    targetAudience: null
                },

                // FCHS Activities (from screenshot) - Sorted by startDate
                {
                    id: 100,
                    title: 'Maternal Mental Health Awareness',
                    description: 'Increasing the awareness regarding maternal mental health.',
                    startDate: null,
                    endDate: null,
                    entity: 'FCHS',
                    department: 'Midwifery',
                    eventType: null,
                    isGeneralActivity: true,
                    color: '#4299e1',
                    location: null,
                    startTime: null,
                    endTime: null,
                    targetAudience: 'Students, Alumni, Academics and Admins'
                },
                {
                    id: 110,
                    title: 'Cultural Day',
                    description: 'Cultural Day is a time to honor our rich traditions, celebrate diversity, and embrace the values that bring communities together.',
                    startDate: '2025-03-26',
                    endDate: '2025-03-26',
                    entity: 'FCHS',
                    department: 'Psychology',
                    eventType: null,
                    isGeneralActivity: true,
                    color: '#4299e1',
                    location: null,
                    startTime: '00:00:00',
                    endTime: '23:59:59',
                    targetAudience: 'All students and faculty'
                },
                {
                    id: 109,
                    title: 'Advisory Board Meeting',
                    description: 'The meeting aims to review the program\'s progress, discuss curriculum updates, address challenges in clinical training and education, and explore opportunities for collaboration to enhance student learning outcomes and professional readiness.',
                    startDate: '2025-06-25',
                    endDate: '2025-06-25',
                    entity: 'FCHS',
                    department: 'Psychology',
                    eventType: null,
                    isGeneralActivity: false,
                    color: '#4299e1',
                    location: null,
                    startTime: '00:00:00',
                    endTime: '23:59:59',
                    targetAudience: 'Psychology Faculty, Director, Academic Director'
                },
                {
                    id: 71,
                    title: 'World Physiotherapy Day',
                    description: 'Celebrating physiotherapist',
                    startDate: '2025-09-08',
                    endDate: '2025-09-08',
                    entity: 'FCHS',
                    department: 'Physiotherapy',
                    eventType: null,
                    isGeneralActivity: true,
                    color: '#4299e1',
                    location: null,
                    startTime: '00:00:00',
                    endTime: '23:59:59',
                    targetAudience: 'Students, Alumni, Academics and Admins'
                },
                {
                    id: 111,
                    title: 'World Physiotherapy day',
                    description: 'The Physiotherapy Department Celebrated World Physiotherapy Day with the global theme "Healthy Aging", highlighting the essential role of physiotherapy in promoting mobility, independence, and quality of life.',
                    startDate: '2025-09-09',
                    endDate: '2025-09-09',
                    entity: 'FCHS',
                    department: 'Physiotherapy',
                    eventType: null,
                    isGeneralActivity: true,
                    color: '#4299e1',
                    location: null,
                    startTime: '00:00:00',
                    endTime: '23:59:59',
                    targetAudience: 'All students and faculty'
                },
                {
                    id: 52,
                    title: 'Healthcare Simulation Week',
                    description: 'FCHS - Emergency Health department will celebrate Simulation week at Ajman Campus on 16th-20th of September 2025. This will include using the advanced technology and virtual reality to organize a state of art simulation challenges for the students and participants. Our partners will provide an award certificate for the winners.',
                    startDate: '2025-09-16',
                    endDate: '2025-09-20',
                    entity: 'FCHS',
                    department: 'Emergency Health',
                    eventType: null,
                    isGeneralActivity: false,
                    color: '#4299e1',
                    location: 'Ajman Campus',
                    startTime: '00:00:00',
                    endTime: '23:59:59',
                    targetAudience: 'Students, Alumni, Academics and Admins'
                },
                {
                    id: 57,
                    title: 'Mass Casualty Incident (MCI) management-Tabletop Simulation Exercise',
                    description: 'A workshop on September 19th where seven fourth-year senior students and their lecturer will attend a mass-casualty tabletop emergency simulated exercise (MCI) and a CBRNE tabletop emergency simulation in Abu Dhabi.',
                    startDate: '2025-09-19',
                    endDate: '2025-09-19',
                    entity: 'FCHS',
                    department: 'Emergency Health',
                    eventType: null,
                    isGeneralActivity: false,
                    color: '#4299e1',
                    location: 'Abu Dhabi',
                    startTime: '00:00:00',
                    endTime: '23:59:59',
                    targetAudience: 'Students, and Academics'
                },
                {
                    id: 58,
                    title: 'Emergency Medicine Conference in Abu Dhabi',
                    description: 'An opportunity for paramedics, students, and staff to attend the \'UpdatED 2025: Emergency Medicine, Trauma, and Critical Care Updates\' conference in Abu Dhabi, sponsored by SEHA and DOH, offering insights into the field.',
                    startDate: '2025-09-20',
                    endDate: '2025-09-21',
                    entity: 'FCHS',
                    department: 'Emergency Health',
                    eventType: null,
                    isGeneralActivity: false,
                    color: '#4299e1',
                    location: 'Abu Dhabi',
                    startTime: '00:00:00',
                    endTime: '23:59:59',
                    targetAudience: 'Students, Academics'
                },
                {
                    id: 102,
                    title: 'World Pharmacists\' Day',
                    description: 'Every year, our Pharmacy Department celebrates World Pharmacists\' Day to highlight the vital role of pharmacists in healthcare. The 2025 theme, "Think Health, Think Pharmacist", emphasizes how pharmacists contribute to better health outcomes, patient safety, and public awareness. By observing this day, we aim to strengthen the professional identity of pharmacists among students, promote community trust, and inspire future leaders in pharmacy practice.',
                    startDate: '2025-09-24',
                    endDate: '2025-09-24',
                    entity: 'FCHS',
                    department: 'Pharmacy',
                    eventType: null,
                    isGeneralActivity: true,
                    color: '#4299e1',
                    location: null,
                    startTime: '00:00:00',
                    endTime: '23:59:59',
                    targetAudience: 'Guest speakers from renowned organizations, Students, Alumni, Academics and Admins'
                },
                {
                    id: 112,
                    title: 'World Heart Day',
                    description: 'Third-year Physiotherapy students will raise awareness about cardiovascular diseases (CVD) and the importance of healthy lifestyles as a key preventive measure against heart disease and stroke.',
                    startDate: '2025-09-29',
                    endDate: '2025-09-29',
                    entity: 'FCHS',
                    department: 'Physiotherapy',
                    eventType: null,
                    isGeneralActivity: true,
                    color: '#4299e1',
                    location: null,
                    startTime: '00:00:00',
                    endTime: '23:59:59',
                    targetAudience: 'All students and faculty'
                },
                {
                    id: 119,
                    title: 'Spinal Cord Injury',
                    description: 'In Collaboration with Specialty Rehabilitation Hospital, Program will feature awareness videos, demonstration of adaptive tools, wheelchair skills, interactive activities for staff and students',
                    startDate: '2025-09-30',
                    endDate: '2025-09-30',
                    entity: 'FCHS',
                    department: 'Physiotherapy',
                    eventType: null,
                    isGeneralActivity: true,
                    color: '#4299e1',
                    location: null,
                    startTime: '00:00:00',
                    endTime: '23:59:59',
                    targetAudience: 'Members drawn from physiotherapy staff both academic and clinical, alumni and a student representative'
                },
                {
                    id: 69,
                    title: 'National Pharmacy Week',
                    description: 'Promoting pharmacy and pharmacists',
                    startDate: '2025-10-01',
                    endDate: '2025-10-31',
                    entity: 'FCHS',
                    department: 'Pharmacy',
                    eventType: null,
                    isGeneralActivity: true,
                    color: '#4299e1',
                    location: null,
                    startTime: '00:00:00',
                    endTime: '23:59:59',
                    targetAudience: 'Students, Alumni, Academics and Admins'
                },
                {
                    id: 60,
                    title: 'Mental Health Awareness Month',
                    description: 'A reminder for paramedics, who work in high-stress environments, to prioritize their mental well-being. Highlighting mental health helps paramedics manage stress and care for themselves and others.',
                    startDate: '2025-10-01',
                    endDate: '2025-10-31',
                    entity: 'FCHS',
                    department: 'Emergency Health',
                    eventType: null,
                    isGeneralActivity: true,
                    color: '#4299e1',
                    location: null,
                    startTime: '00:00:00',
                    endTime: '23:59:59',
                    targetAudience: 'Students, Alumni, Academics and Admins'
                },
                {
                    id: 105,
                    title: 'Breast Cancer Awareness Month',
                    description: 'Throughout October, we join the global effort to raise awareness about breast cancer, focusing on early detection, prevention, and support for patients. The Pharmacy Department organizes activities to highlight the role of pharmacists in patient education, medication management, and support for survivors. By observing this month, we aim to build compassionate pharmacists who can contribute to cancer awareness, screening advocacy, and holistic care.',
                    startDate: '2025-10-01',
                    endDate: '2025-10-31',
                    entity: 'FCHS',
                    department: 'Pharmacy',
                    eventType: null,
                    isGeneralActivity: true,
                    color: '#4299e1',
                    location: null,
                    startTime: '00:00:00',
                    endTime: '23:59:59',
                    targetAudience: 'Students, Academics and Admins'
                },
                {
                    id: 113,
                    title: 'Breast Cancer Awareness Day',
                    description: 'Third year physiotherapy students will lead this activity about importance of exercises and physical activites for prevention and managment.',
                    startDate: '2025-10-01',
                    endDate: '2025-10-31',
                    entity: 'FCHS',
                    department: 'Physiotherapy',
                    eventType: null,
                    isGeneralActivity: true,
                    color: '#4299e1',
                    location: null,
                    startTime: '00:00:00',
                    endTime: '23:59:59',
                    targetAudience: 'All students and faculty'
                },
                {
                    id: 59,
                    title: 'CPR awareness Month',
                    description: 'Celebrated annually from June 1-7 as National CPR and AED Awareness Week, highlighting the role of CPR and AED in saving lives. Activities across three campuses include a 10-minute speech on community awareness regarding CPR and AED, and an AHA BLS course for 10-20 students at each campus.',
                    startDate: '2025-10-07',
                    endDate: '2025-10-07',
                    entity: 'FCHS',
                    department: 'Emergency Health',
                    eventType: null,
                    isGeneralActivity: true,
                    color: '#4299e1',
                    location: 'Three Campuses',
                    startTime: '00:00:00',
                    endTime: '23:59:59',
                    targetAudience: 'Students, Alumni, Academics and Admins'
                },
                {
                    id: 68,
                    title: 'Breast Cancer Awareness Month',
                    description: 'The Radiography Department will host Breast Cancer Awareness Day, featuring interactive booths managed by volunteering students. The event will focus on key topics, including: Breast Cancer Overview: Risk factors, symptoms, and prevention. Role of Imaging: Mammography, ultrasound, and advanced diagnostic tools. Self-Examination Techniques: Promoting early detection practices. Lifestyle and Breast Health: The impact of nutrition and lifestyle. Advances in Radiography: Innovations in breast cancer imaging. This event aims to raise awareness, promote early detection, and highlight the vital role of radiography in breast cancer diagnosis and management.',
                    startDate: '2025-10-08',
                    endDate: '2025-10-08',
                    entity: 'FCHS',
                    department: 'RMI and Nursing',
                    eventType: null,
                    isGeneralActivity: true,
                    color: '#4299e1',
                    location: null,
                    startTime: '00:00:00',
                    endTime: '23:59:59',
                    targetAudience: 'Students, Alumni, Academics and Admins'
                },
                {
                    id: 107,
                    title: 'World Mental Health Day',
                    description: 'World Mental Health Day reminds us to prioritize emotional well-being, raise awareness, and break the stigma around seeking help. celebrate it to encourage open conversations, promote mental health support, and create a more compassionate, healthier world.',
                    startDate: '2025-10-09',
                    endDate: '2025-10-09',
                    entity: 'FCHS',
                    department: 'Psychology',
                    eventType: null,
                    isGeneralActivity: true,
                    color: '#4299e1',
                    location: null,
                    startTime: '00:00:00',
                    endTime: '23:59:59',
                    targetAudience: 'All students and faculty'
                },
                {
                    id: 74,
                    title: 'World Sight Day',
                    description: '#LoveYourEyes — Put People at Its Heart',
                    startDate: '2025-10-09',
                    endDate: '2025-10-09',
                    entity: 'FCHS',
                    department: 'Nursing',
                    eventType: null,
                    isGeneralActivity: true,
                    color: '#4299e1',
                    location: null,
                    startTime: '00:00:00',
                    endTime: '23:59:59',
                    targetAudience: 'Students, Alumni, Academics and Admins'
                },
                {
                    id: 73,
                    title: 'World Mental Health Day',
                    description: 'Raising awareness among UAE communities including the students and Faculty. Access to Services — Mental Health in Catastrophes and Emergencies.',
                    startDate: '2025-10-10',
                    endDate: '2025-10-10',
                    entity: 'FCHS',
                    department: 'Nursing',
                    eventType: null,
                    isGeneralActivity: true,
                    color: '#4299e1',
                    location: null,
                    startTime: '00:00:00',
                    endTime: '23:59:59',
                    targetAudience: 'Students, Alumni, Academics and Admins'
                },
                {
                    id: 65,
                    title: 'World Mental Health Day',
                    description: 'Raising awareness of mental health issues',
                    startDate: '2025-10-10',
                    endDate: '2025-10-10',
                    entity: 'FCHS',
                    department: 'Psychology and Nursing',
                    eventType: null,
                    isGeneralActivity: true,
                    color: '#4299e1',
                    location: null,
                    startTime: '00:00:00',
                    endTime: '23:59:59',
                    targetAudience: 'Students, Alumni, Academics and Admins'
                },
                {
                    id: 75,
                    title: 'World Arthritis Day',
                    description: 'To inform the communities about arthritis and prevention and focus on the theme, It’s in your hands, take action.',
                    startDate: '2025-10-12',
                    endDate: '2025-10-12',
                    entity: 'FCHS',
                    department: 'Nursing',
                    eventType: null,
                    isGeneralActivity: true,
                    color: '#4299e1',
                    location: null,
                    startTime: '00:00:00',
                    endTime: '23:59:59',
                    targetAudience: 'Students, Alumni, Academics and Admins'
                },
                {
                    id: 76,
                    title: 'Global Handwashing Day',
                    description: 'To demonstrate the hand hygiene and allow practice. Core message: handwashing with soap saves lives.',
                    startDate: '2025-10-15',
                    endDate: '2025-10-15',
                    entity: 'FCHS',
                    department: 'Nursing',
                    eventType: null,
                    isGeneralActivity: true,
                    color: '#4299e1',
                    location: null,
                    startTime: '00:00:00',
                    endTime: '23:59:59',
                    targetAudience: 'Students, Alumni, Academics and Admins'
                },
                {
                    id: 117,
                    title: 'Physiotherapy Managers meet A',
                    description: 'Meeting physiotherapy managers to discuss the new study plan and clinical placement arrangements',
                    startDate: '2025-10-15',
                    endDate: '2025-10-15',
                    entity: 'FCHS',
                    department: 'Physiotherapy',
                    eventType: null,
                    isGeneralActivity: false,
                    color: '#4299e1',
                    location: null,
                    startTime: '00:00:00',
                    endTime: '23:59:59',
                    targetAudience: 'Staff and managers'
                },
                {
                    id: 77,
                    title: 'International Infection Prevention',
                    description: 'To discribe and demonstrate IPC practices and focus on the theme, STAND UP for IP (APIC)',
                    startDate: '2025-10-19',
                    endDate: '2025-10-25',
                    entity: 'FCHS',
                    department: 'Nursing',
                    eventType: null,
                    isGeneralActivity: true,
                    color: '#4299e1',
                    location: null,
                    startTime: '00:00:00',
                    endTime: '23:59:59',
                    targetAudience: 'Students, Alumni, Academics and Admins'
                },
                {
                    id: 67,
                    title: 'World Osteoporosis Day',
                    description: 'The Radiography Department will host Osteoporosis Awareness Day, featuring interactive booths managed by volunteering students. The event will address key topics, including: Osteoporosis Overview: Causes, risk factors, and prevention. Imaging Techniques: The role of DXA and other modalities in diagnosis. Bone Health: The impact of lifestyle on bone density. Osteoporosis and Menopause: Hormonal influences on bone health. Radiation Safety: Best practices for safe imaging. The event aims to promote awareness of osteoporosis, highlight the importance of radiography in early detection and management, and support community health education.',
                    startDate: '2025-10-20',
                    endDate: '2025-10-20',
                    entity: 'FCHS',
                    department: 'Nursing',
                    eventType: null,
                    isGeneralActivity: true,
                    color: '#4299e1',
                    location: null,
                    startTime: '00:00:00',
                    endTime: '23:59:59',
                    targetAudience: 'Students, Alumni, Academics and Admins'
                },
                {
                    id: 104,
                    title: 'World Osteoporosis Day',
                    description: 'World Osteoporosis Day reminds us of the importance of bone health and fracture prevention. Our Pharmacy Department observes this day to create awareness about lifestyle, nutrition, and medication strategies that can help prevent osteoporosis. The objective is to train pharmacy students to counsel patients on preventive care and to promote community well-being through early education and intervention.',
                    startDate: '2025-10-20',
                    endDate: '2025-10-20',
                    entity: 'FCHS',
                    department: 'Pharmacy',
                    eventType: null,
                    isGeneralActivity: true,
                    color: '#4299e1',
                    location: null,
                    startTime: '00:00:00',
                    endTime: '23:59:59',
                    targetAudience: 'Students, Academics and Admins'
                },
                {
                    id: 66,
                    title: 'Menopause Month',
                    description: 'Educating all women including the female relatives of students about menopause',
                    startDate: '2025-10-23',
                    endDate: '2025-10-23',
                    entity: 'FCHS',
                    department: 'Midwifery',
                    eventType: null,
                    isGeneralActivity: true,
                    color: '#4299e1',
                    location: null,
                    startTime: '00:00:00',
                    endTime: '23:59:59',
                    targetAudience: 'Students, Alumni, Academics and Admins'
                },
                {
                    id: 114,
                    title: 'Spina Bifida Awareness Day',
                    description: 'Second year Physiotherapy students will lead an interactive awareness day.',
                    startDate: '2025-10-25',
                    endDate: '2025-10-25',
                    entity: 'FCHS',
                    department: 'Physiotherapy',
                    eventType: null,
                    isGeneralActivity: true,
                    color: '#4299e1',
                    location: null,
                    startTime: '00:00:00',
                    endTime: '23:59:59',
                    targetAudience: 'All students and faculty'
                },
                {
                    id: 78,
                    title: 'World Stroke Day',
                    description: 'Every Minute Counts',
                    startDate: '2025-10-29',
                    endDate: '2025-10-29',
                    entity: 'FCHS',
                    department: 'Nursing',
                    eventType: null,
                    isGeneralActivity: true,
                    color: '#4299e1',
                    location: null,
                    startTime: '00:00:00',
                    endTime: '23:59:59',
                    targetAudience: 'Students, Alumni, Academics and Admins'
                },
                {
                    id: 53,
                    title: 'World Radiography Day (WRD)',
                    description: 'The International Day of Radiology will take place on November 8 with the aim of building greater awareness of the value that radiology contributes to safe patient care and improving public understanding of the vital role radiologists and radiographers play in the healthcare continuum.',
                    startDate: '2025-10-29',
                    endDate: '2025-10-29',
                    entity: 'FCHS',
                    department: 'RMI',
                    eventType: null,
                    isGeneralActivity: false,
                    color: '#4299e1',
                    location: null,
                    startTime: '00:00:00',
                    endTime: '23:59:59',
                    targetAudience: 'Students, Alumni, Academics and Admins',
                },
                {
                    id: 70,
                    title: 'Antimicrobial Awareness Week',
                    description: 'Raising awareness of antimicrobial resistance and appropriate use of antimicrobials',
                    startDate: '2025-11-01',
                    endDate: '2025-11-30',
                    entity: 'FCHS',
                    department: 'Pharmacy and Nursing',
                    eventType: null,
                    isGeneralActivity: true,
                    color: '#4299e1',
                    location: null,
                    startTime: '00:00:00',
                    endTime: '23:59:59',
                    targetAudience: 'Students, Alumni, Academics and Admins'
                },
                {
                    id: 79,
                    title: 'November (Men\'s Health Awareness)',
                    description: 'Changing the face of men\'s health',
                    startDate: '2025-11-01',
                    endDate: '2025-11-30',
                    entity: 'FCHS',
                    department: 'Nursing',
                    eventType: null,
                    isGeneralActivity: true,
                    color: '#4299e1',
                    location: null,
                    startTime: '00:00:00',
                    endTime: '23:59:59',
                    targetAudience: 'Students, Alumni, Academics and Admins'
                },
                {
                    id: 80,
                    title: 'Lung Cancer Awareness Month',
                    description: 'Stand Strong Against Lung Cancer',
                    startDate: '2025-11-01',
                    endDate: '2025-11-30',
                    entity: 'FCHS',
                    department: 'Nursing',
                    eventType: null,
                    isGeneralActivity: true,
                    color: '#4299e1',
                    location: null,
                    startTime: '00:00:00',
                    endTime: '23:59:59',
                    targetAudience: 'Students, Alumni, Academics and Admins'
                },
                {
                    id: 118,
                    title: 'Advisory Board Meeting',
                    description: 'Members drawn from physiotherapy staff both academic and clinical, alumni and a student representative. The meeting focus on current curricular practices, room for any change and advising systems.',
                    startDate: '2025-11-01',
                    endDate: '2025-11-30',
                    entity: 'FCHS',
                    department: 'Physiotherapy',
                    eventType: null,
                    isGeneralActivity: false,
                    color: '#4299e1',
                    location: null,
                    startTime: '00:00:00',
                    endTime: '23:59:59',
                    targetAudience: 'Members drawn from physiotherapy staff both academic and clinical, alumni and a student representative'
                },
                {
                    id: 82,
                    title: 'National Lung Cancer Screening',
                    description: 'Promote lung cancer screening awareness',
                    startDate: '2025-11-08',
                    endDate: '2025-11-08',
                    entity: 'FCHS',
                    department: 'Nursing',
                    eventType: null,
                    isGeneralActivity: true,
                    color: '#4299e1',
                    location: null,
                    startTime: '00:00:00',
                    endTime: '23:59:59',
                    targetAudience: 'Students, Alumni, Academics and Admins'
                },
                {
                    id: 81,
                    title: 'World Diabetes Day',
                    description: 'Diabetes and well-being (focus: Diabetes and the workplace)',
                    startDate: '2025-11-14',
                    endDate: '2025-11-14',
                    entity: 'FCHS',
                    department: 'Nursing',
                    eventType: null,
                    isGeneralActivity: true,
                    color: '#4299e1',
                    location: null,
                    startTime: '00:00:00',
                    endTime: '23:59:59',
                    targetAudience: 'Students, Alumni, Academics and Admins'
                },
                {
                    id: 72,
                    title: 'World Prematurity Day',
                    description: 'Educating students and faculty about premature birth and the challenges  to the newborn long term and the development of technology improving outcomes.',
                    startDate: '2025-11-17',
                    endDate: '2025-11-17',
                    entity: 'FCHS',
                    department: 'Midwifery',
                    eventType: null,
                    isGeneralActivity: true,
                    color: '#4299e1',
                    location: null,
                    startTime: '00:00:00',
                    endTime: '23:59:59',
                    targetAudience: 'Students, Alumni, Academics and Admins'
                },
                {
                    id: 83,
                    title: 'World Prematurity Day',
                    description: 'Awareness of preterm birth (theme TBA)',
                    startDate: '2025-11-17',
                    endDate: '2025-11-17',
                    entity: 'FCHS',
                    department: 'Nursing',
                    eventType: null,
                    isGeneralActivity: true,
                    color: '#4299e1',
                    location: null,
                    startTime: '00:00:00',
                    endTime: '23:59:59',
                    targetAudience: 'Students, Alumni, Academics and Admins'
                },
                {
                    id: 101,
                    title: 'World Prematurity Day',
                    description: 'Educating students and faculty about premature birth and the challenges to the newborn long term and the development of technology improving outcomes.',
                    startDate: '2025-11-17',
                    endDate: '2025-11-17',
                    entity: 'FCHS',
                    department: 'Midwifery',
                    eventType: null,
                    isGeneralActivity: true,
                    color: '#4299e1',
                    location: null,
                    startTime: '00:00:00',
                    endTime: '23:59:59',
                    targetAudience: 'Students, Alumni, Academics and Admins'
                },
                {
                    id: 84,
                    title: 'World AMR Awareness Week',
                    description: 'Raising awareness on Antimicrobial resistance awareness (theme TBA)',
                    startDate: '2025-11-18',
                    endDate: '2025-11-24',
                    entity: 'FCHS',
                    department: 'Nursing',
                    eventType: null,
                    isGeneralActivity: true,
                    color: '#4299e1',
                    location: null,
                    startTime: '00:00:00',
                    endTime: '23:59:59',
                    targetAudience: 'Students, Alumni, Academics and Admins'
                },
                {
                    id: 103,
                    title: 'World AMR (Antimicrobial Resistance) Awareness Week',
                    description: 'This event focuses on raising awareness about the growing threat of antimicrobial resistance (AMR). As a pharmacy department, we use this platform to educate students and the community on rational antibiotic use, infection prevention, and stewardship programs. The objective is to empower future pharmacists to play a proactive role in combating AMR, thereby safeguarding medicines\' effectiveness for generations to come.',
                    startDate: '2025-11-18',
                    endDate: '2025-11-24',
                    entity: 'FCHS',
                    department: 'Pharmacy',
                    eventType: null,
                    isGeneralActivity: true,
                    color: '#4299e1',
                    location: null,
                    startTime: '00:00:00',
                    endTime: '23:59:59',
                    targetAudience: 'Students, & Academics'
                },
                {
                    id: 85,
                    title: 'World COPD Day',
                    description: 'To share knowledge and raise awareness on Short of Breath, Think COPD',
                    startDate: '2025-11-19',
                    endDate: '2025-11-19',
                    entity: 'FCHS',
                    department: 'Nursing',
                    eventType: null,
                    isGeneralActivity: true,
                    color: '#4299e1',
                    location: null,
                    startTime: '00:00:00',
                    endTime: '23:59:59',
                    targetAudience: 'Students, Alumni, Academics and Admins'
                },
                {
                    id: 115,
                    title: 'World COPD Day',
                    description: 'Physiotherapy students raising awareness on common symptoms of shortness of breath and engaging people to consider COPD as a cause, as the disease is often underdiagnosed.',
                    startDate: '2025-11-19',
                    endDate: '2025-11-19',
                    entity: 'FCHS',
                    department: 'Physiotherapy',
                    eventType: null,
                    isGeneralActivity: true,
                    color: '#4299e1',
                    location: null,
                    startTime: '00:00:00',
                    endTime: '23:59:59',
                    targetAudience: 'All students and faculty'
                },
                {
                    id: 54,
                    title: 'Smarter Healthcare: AI & Lung Cancer',
                    description: 'Explore how AI is shaping radiology and healthcare by improving lung cancer awareness, early detection, and patient outcomes.',
                    startDate: '2025-11-19',
                    endDate: '2025-11-19',
                    entity: 'FCHS',
                    department: 'RMI',
                    eventType: null,
                    isGeneralActivity: false,
                    color: '#4299e1',
                    location: null,
                    startTime: '00:00:00',
                    endTime: '23:59:59',
                    targetAudience: 'Students, Alumni, Academics and Admins',
                },
                {
                    id: 108,
                    title: 'Advisory Board Meeting',
                    description: 'The meeting aims to review the program\'s progress, discuss curriculum updates, address challenges in clinical training and education, and explore opportunities for collaboration to enhance student learning outcomes and professional readiness.',
                    startDate: '2025-12-25',
                    endDate: '2025-12-25',
                    entity: 'FCHS',
                    department: 'Psychology',
                    eventType: null,
                    isGeneralActivity: false,
                    color: '#4299e1',
                    location: null,
                    startTime: '00:00:00',
                    endTime: '23:59:59',
                    targetAudience: 'Psychology Faculty, Director, Academic Director'
                },
                {
                    id: 61,
                    title: 'UAE Innovation Month Celebration',
                    description: 'As part of UAE Innovation Month, Paramedic students at Fatima College of Health Sciences (FCHS) celebrated creativity and advancement in healthcare through simulation challenges, student-driven projects, and knowledge-sharing, highlighting innovation\'s role in Emergency Medical Services (EMS).',
                    startDate: '2026-02-01',
                    endDate: '2026-02-28',
                    entity: 'FCHS',
                    department: 'Emergency Health',
                    eventType: null,
                    isGeneralActivity: true,
                    color: '#4299e1',
                    location: null,
                    startTime: '00:00:00',
                    endTime: '23:59:59',
                    targetAudience: 'Students, Alumni, Academics and Admins'
                },
                {
                    id: 86,
                    title: 'World Cancer Day',
                    description: 'Close the Care Gap',
                    startDate: '2026-02-04',
                    endDate: '2026-02-04',
                    entity: 'FCHS',
                    department: 'Nursing',
                    eventType: null,
                    isGeneralActivity: true,
                    color: '#4299e1',
                    location: null,
                    startTime: '00:00:00',
                    endTime: '23:59:59',
                    targetAudience: 'Students, Alumni, Academics and Admins'
                },
                {
                    id: 62,
                    title: 'Nutrition Month',
                    description: 'In honor of Nutrition Month this March, we\'ll be hosting an event to highlight the importance of good nutrition. It\'s a chance to emphasize how essential nutrition is for overall well-being and daily performance.',
                    startDate: '2026-03-01',
                    endDate: '2026-03-31',
                    entity: 'FCHS',
                    department: 'Emergency Health',
                    eventType: null,
                    isGeneralActivity: true,
                    color: '#4299e1',
                    location: null,
                    startTime: '00:00:00',
                    endTime: '23:59:59',
                    targetAudience: 'Students, Alumni, Academics and Partners'
                },
                {
                    id: 87,
                    title: 'World Hearing Day',
                    description: 'Changing mindsets: Let\'s make ear and hearing care a reality for all',
                    startDate: '2026-03-03',
                    endDate: '2026-03-03',
                    entity: 'FCHS',
                    department: 'Nursing',
                    eventType: null,
                    isGeneralActivity: true,
                    color: '#4299e1',
                    location: null,
                    startTime: '00:00:00',
                    endTime: '23:59:59',
                    targetAudience: 'Students, Alumni, Academics and Admins'
                },
                {
                    id: 97,
                    title: 'International Women Day',
                    description: 'Celebrating the achievements of women. The date is on a Sunday so need',
                    startDate: '2026-03-08',
                    endDate: '2026-03-08',
                    entity: 'FCHS',
                    department: 'All',
                    eventType: null,
                    isGeneralActivity: true,
                    color: '#4299e1',
                    location: null,
                    startTime: '00:00:00',
                    endTime: '23:59:59',
                    targetAudience: 'Students, Alumni, Academics and Admins'
                },
                {
                    id: 98,
                    title: 'Safer Sleep',
                    description: 'Included as part of the Health promotion course educating FCHS student',
                    startDate: '2026-03-09',
                    endDate: '2026-03-15',
                    entity: 'FCHS',
                    department: 'Midwifery',
                    eventType: null,
                    isGeneralActivity: true,
                    color: '#4299e1',
                    location: null,
                    startTime: '00:00:00',
                    endTime: '23:59:59',
                    targetAudience: 'Students, Alumni, Academics and Admins'
                },
                {
                    id: 88,
                    title: 'World Kidney Day',
                    description: 'Theme to be announced',
                    startDate: '2026-03-12',
                    endDate: '2026-03-12',
                    entity: 'FCHS',
                    department: 'Nursing',
                    eventType: null,
                    isGeneralActivity: true,
                    color: '#4299e1',
                    location: null,
                    startTime: '00:00:00',
                    endTime: '23:59:59',
                    targetAudience: 'Students, Alumni, Academics and Admins'
                },

                {
                    id: 89,
                    title: 'World Tuberculosis Day',
                    description: 'Yes! We can end TB',
                    startDate: '2026-03-24',
                    endDate: '2026-03-24',
                    entity: 'FCHS',
                    department: 'Nursing',
                    eventType: null,
                    isGeneralActivity: true,
                    color: '#4299e1',
                    location: null,
                    startTime: '00:00:00',
                    endTime: '23:59:59',
                    targetAudience: 'Students, Alumni, Academics and Admins'
                },
                {
                    id: 55,
                    title: 'Stakeholders Meeting – Abu Dhabi Campus',
                    description: 'The Stakeholders Meeting is a strategic forum designed to bring together faculty, industry partners, and key stakeholders. The meeting aims to review the program’s progress, discuss curriculum updates, address challenges in clinical training and education, and explore opportunities for collaboration to enhance student learning outcomes and professional readiness. Stakeholder feedback will play a vital role in shaping the future direction of the program.',
                    startDate: '2026-03-24',
                    endDate: '2026-03-24',
                    entity: 'FCHS',
                    department: 'RMI',
                    eventType: null,
                    isGeneralActivity: false,
                    color: '#4299e1',
                    location: 'Abu Dhabi Campus',
                    startTime: '00:00:00',
                    endTime: '23:59:59',
                    targetAudience: 'Academics and industry partners',
                },
                {
                    id: 56,
                    title: 'Stakeholders Meeting – Al Ain Campus',
                    description: 'The Stakeholders Meeting is a strategic forum designed to bring together faculty, industry partners, and key stakeholders. The meeting aims to review the program’s progress, discuss curriculum updates, address challenges in clinical training and education, and explore opportunities for collaboration to enhance student learning outcomes and professional readiness. Stakeholder feedback will play a vital role in shaping the future direction of the program.',
                    startDate: '2026-03-25',
                    endDate: '2026-03-25',
                    entity: 'FCHS',
                    department: 'RMI',
                    eventType: null,
                    isGeneralActivity: false,
                    color: '#4299e1',
                    location: 'Al Ain Campus',
                    startTime: '00:00:00',
                    endTime: '23:59:59',
                    targetAudience: 'Academics and industry partners',
                },
                {
                    id: 106,
                    title: 'Cosmetic Awareness Campaign',
                    description: 'The Pharmacy Department celebrates the Cosmetic Awareness Campaign to educate students and the community about safe, effective, and evidence-based use of cosmetic products. We highlight the pharmacist\'s role in ensuring product quality, understanding active ingredients, and preventing misuse or harmful side effects. The campaign also focuses on skin health, regulatory guidelines, and ethical aspects of cosmetic use. Our objective is to prepare pharmacy graduates to guide patients with scientifically sound advice and to promote safe cosmetic practices in society.',
                    startDate: '2026-04-01',
                    endDate: '2026-04-30',
                    entity: 'FCHS',
                    department: 'Pharmacy',
                    eventType: null,
                    isGeneralActivity: true,
                    color: '#4299e1',
                    location: null,
                    startTime: '00:00:00',
                    endTime: '23:59:59',
                    targetAudience: 'Students, Academics'
                },
                {
                    id: 90,
                    title: 'World Health Day',
                    description: 'Theme to be announced',
                    startDate: '2026-04-07',
                    endDate: '2026-04-07',
                    entity: 'FCHS',
                    department: 'Nursing',
                    eventType: null,
                    isGeneralActivity: true,
                    color: '#4299e1',
                    location: null,
                    startTime: '00:00:00',
                    endTime: '23:59:59',
                    targetAudience: 'Students, Alumni, Academics and Admins'
                },
                {
                    id: 91,
                    title: 'World Immunization Week',
                    description: 'Humanly Possible: Immunization for All',
                    startDate: '2026-04-20',
                    endDate: '2026-04-26',
                    entity: 'FCHS',
                    department: 'Nursing',
                    eventType: null,
                    isGeneralActivity: true,
                    color: '#4299e1',
                    location: null,
                    startTime: '00:00:00',
                    endTime: '23:59:59',
                    targetAudience: 'Students, Alumni, Academics and Admins'
                },
                {
                    id: 92,
                    title: 'World Malaria Day',
                    description: 'Accelerate the fight against malaria for a more equitable world',
                    startDate: '2026-04-25',
                    endDate: '2026-04-25',
                    entity: 'FCHS',
                    department: 'Nursing',
                    eventType: null,
                    isGeneralActivity: true,
                    color: '#4299e1',
                    location: null,
                    startTime: '00:00:00',
                    endTime: '23:59:59',
                    targetAudience: 'Students, Alumni, Academics and Admins'
                },
                {
                    id: 63,
                    title: 'Future Paramedics Showcase: Capstone Projects',
                    description: 'This event highlights the hard work, innovation, and clinical excellence of our paramedic students as they present their final capstone projects. It serves as a platform to showcase research, simulations, and practical solutions that reflect their readiness to contribute to emergency medical services. Faculty, peers, and industry professionals will gather to celebrate their achievements and inspire the next generation of paramedics.',
                    startDate: '2026-05-01',
                    endDate: '2026-05-31',
                    entity: 'FCHS',
                    department: 'Emergency Health',
                    eventType: null,
                    isGeneralActivity: false,
                    color: '#4299e1',
                    location: null,
                    startTime: '00:00:00',
                    endTime: '23:59:59',
                    targetAudience: 'Students, Alumni, Academics and Partners'
                },
                {
                    id: 116,
                    title: 'Stakeholder meeting',
                    description: 'Meeting with the managers to discuss curriculum, employment opportunities and plan placements for the next academic years',
                    startDate: '2026-05-01',
                    endDate: '2026-05-31',
                    entity: 'FCHS',
                    department: 'Physiotherapy',
                    eventType: null,
                    isGeneralActivity: false,
                    color: '#4299e1',
                    location: null,
                    startTime: '00:00:00',
                    endTime: '23:59:59',
                    targetAudience: 'Physiotherapy staff and industrial partners'
                },
                {
                    id: 93,
                    title: 'World Hand Hygiene Day',
                    description: 'Why is sharing knowledge about hand hygiene still so important?',
                    startDate: '2026-05-05',
                    endDate: '2026-05-05',
                    entity: 'FCHS',
                    department: 'Nursing',
                    eventType: null,
                    isGeneralActivity: true,
                    color: '#4299e1',
                    location: null,
                    startTime: '00:00:00',
                    endTime: '23:59:59',
                    targetAudience: 'Students, Alumni, Academics and Admins'
                },
                {
                    id: 99,
                    title: 'International Day of the Midwife',
                    description: 'Celebrating midwifery and joining all the midwives voices internationally',
                    startDate: '2026-05-05',
                    endDate: '2026-05-05',
                    entity: 'FCHS',
                    department: 'Midwifery',
                    eventType: null,
                    isGeneralActivity: true,
                    color: '#4299e1',
                    location: null,
                    startTime: '00:00:00',
                    endTime: '23:59:59',
                    targetAudience: 'Students, Alumni, Academics and Admins'
                },
                {
                    id: 94,
                    title: 'World Hypertension Day',
                    description: 'Measure Your Blood Pressure Accurately, Control It, Live Longer',
                    startDate: '2026-05-17',
                    endDate: '2026-05-17',
                    entity: 'FCHS',
                    department: 'Nursing',
                    eventType: null,
                    isGeneralActivity: true,
                    color: '#4299e1',
                    location: null,
                    startTime: '00:00:00',
                    endTime: '23:59:59',
                    targetAudience: 'Students, Alumni, Academics and Admins'
                },
                {
                    id: 95,
                    title: 'World No Tobacco Day',
                    description: 'Protecting children from tobacco industry interference',
                    startDate: '2026-05-31',
                    endDate: '2026-05-31',
                    entity: 'FCHS',
                    department: 'Nursing',
                    eventType: null,
                    isGeneralActivity: true,
                    color: '#4299e1',
                    location: null,
                    startTime: '00:00:00',
                    endTime: '23:59:59',
                    targetAudience: 'Students, Alumni, Academics and Admins'
                },
                {
                    id: 64,
                    title: 'World Blood Donor Day',
                    description: 'In honor of World Blood Donor Day on June 14th, we will organize an event at our college to highlight the vital role of paramedics in emergency blood transfusions and the importance of blood donation. It\'ll be a great way to raise awareness and involve our community.',
                    startDate: '2026-06-14',
                    endDate: '2026-06-14',
                    entity: 'FCHS',
                    department: 'Emergency Health',
                    eventType: null,
                    isGeneralActivity: true,
                    color: '#4299e1',
                    location: null,
                    startTime: '00:00:00',
                    endTime: '23:59:59',
                    targetAudience: 'Students, Alumni, Academics and Partners'
                },
                {
                    id: 96,
                    title: 'World Blood Donor Day',
                    description: '20 years of celebrating giving: thank you blood donors!',
                    startDate: '2026-06-14',
                    endDate: '2026-06-14',
                    entity: 'FCHS',
                    department: 'Nursing',
                    eventType: null,
                    isGeneralActivity: true,
                    color: '#4299e1',
                    location: null,
                    startTime: '00:00:00',
                    endTime: '23:59:59',
                    targetAudience: 'Students, Alumni, Academics and Admins'
                },
            ]
        }
    },
    computed: {
        filteredActivities() {
            let activities = this.activities;

            if (this.selectedEntity !== '') {
                activities = activities.filter(activity => activity.entity === this.selectedEntity);
            }

            return activities;
        },
        hasFilteredActivities() {
            return this.filteredActivities.length > 0;
        },
        getFilteredEntities() {
            const entities = [...new Set(this.filteredActivities.map(activity => activity.entity))];
            return entities;
        }
    },
    watch: {
        filteredActivities: {
            handler() {
                this.updateCalendarEvents();
            },
            immediate: true
        }
    },
    methods: {
        getFilteredActivitiesByEntity(entity) {
            return this.filteredActivities.filter(activity => activity.entity === entity);
        },
        getEntityName(entity) {
            const entityNames = {
                'ADPOLY': 'Abu Dhabi Polytechnic',
                'FCHS': 'Fatima College of Health Sciences',
                'ATHS': 'Applied Technology Schools',
                'ATS': 'Applied Technology Schools'
            };
            return entityNames[entity] || entity;
        },
        updateCalendarEvents() {
            const calendarEvents = this.filteredActivities.map(activity => ({
                id: activity.id,
                title: activity.title,
                start: activity.startDate,
                end: activity.endDate,
                backgroundColor: activity.color,
                borderColor: activity.color,
                textColor: '#ffffff',
                extendedProps: {
                    description: activity.description,
                    entity: activity.entity,
                    department: activity.department,
                    isGeneralActivity: activity.isGeneralActivity
                }
            }));

            this.calendarOptions.events = calendarEvents;
        }
    }
}
</script>

<style lang="scss" scoped>
.bg-1 {
    background: linear-gradient(90deg, rgba(34, 42, 57, 1) 0%, rgba(10, 66, 117, 1) 100%);
}

.fade-enter-active,
.fade-leave-active {
    transition: opacity 0.3s ease;
}

.fade-enter-from,
.fade-leave-to {
    opacity: 0;
}

:deep(.fc) {
    .fc-event {
        border-radius: 4px;
        font-size: 0.75rem;
        padding: 2px 4px;
    }

    .fc-daygrid-event {
        margin: 1px 0;
    }

    .fc-event-title {
        font-weight: 500;
    }

    .fc-toolbar-title {
        font-size: 1.5rem;
        font-weight: 600;
        color: #374151;
    }

    .fc-button {
        background-color: #394867;
        border-color: #394867;
        font-weight: 500;

        &:hover {
            background-color: #2d3748;
            border-color: #2d3748;
        }

        &.fc-button-active {
            background-color: #1d337c;
            border-color: #1d337c;
        }
    }

    .fc-daygrid-day-number {
        color: #374151;
        font-weight: 500;
    }

    .fc-col-header-cell {
        background-color: #f9fafb;
        color: #374151;
        font-weight: 600;
    }
}
</style>
